import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  NavigateNext,
  PlayArrow,
  Pause,
  Stop,
  Refresh,
  Visibility,
  Add,
} from '@mui/icons-material';
import {
  useGetCampaignsQuery,
  useStartCampaignMutation,
  usePauseCampaignMutation,
  useResumeCampaignMutation,
  useStopCampaignMutation,
  Campaign,
} from '../store/api/outboundCallingApi';
import { useGetActiveSynthflowAccountsQuery } from '../store/api/synthflowApi';
import { useGetAgentsForAccountQuery } from '../store/api/synthflowAgentsApi';

const AllCampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loadingCampaignId, setLoadingCampaignId] = useState<number | null>(null);
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [campaignToStop, setCampaignToStop] = useState<Campaign | null>(null);

  // API queries
  const { data: campaigns, isLoading: campaignsLoading, error: campaignsError, refetch: refetchCampaigns } = useGetCampaignsQuery({});
  const { data: accounts = [] } = useGetActiveSynthflowAccountsQuery();

  // Mutations
  const [startCampaign] = useStartCampaignMutation();
  const [pauseCampaign] = usePauseCampaignMutation();
  const [resumeCampaign] = useResumeCampaignMutation();
  const [stopCampaign] = useStopCampaignMutation();

  const handleViewDetails = (campaignId: number) => {
    navigate(`/outbound-calling/campaign/${campaignId}`);
  };

  const handleCampaignAction = async (campaignId: number, action: string) => {
    try {
      setError(null);
      setSuccess(null);
      setLoadingCampaignId(campaignId);
      
      switch (action) {
        case 'start':
          await startCampaign(campaignId).unwrap();
          setSuccess('Campaign started successfully!');
          break;
        case 'pause':
          await pauseCampaign(campaignId).unwrap();
          setSuccess('Campaign paused successfully!');
          break;
        case 'resume':
          await resumeCampaign(campaignId).unwrap();
          setSuccess('Campaign resumed successfully!');
          break;
        case 'stop':
          await stopCampaign(campaignId).unwrap();
          setSuccess('Campaign stopped successfully!');
          break;
      }
      
      // Refetch campaigns to update UI
      await refetchCampaigns();
      
      // Clear success message after a delay
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      let errorMessage = 'An error occurred';
      if (err.data?.detail) {
        errorMessage = err.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(`Failed to ${action} campaign: ${errorMessage}`);
    } finally {
      setLoadingCampaignId(null);
    }
  };

  const handleStopClick = (campaign: Campaign) => {
    setCampaignToStop(campaign);
    setStopDialogOpen(true);
  };

  const handleStopConfirm = () => {
    if (campaignToStop) {
      handleCampaignAction(campaignToStop.id, 'stop');
      setStopDialogOpen(false);
      setCampaignToStop(null);
    }
  };

  const handleStopCancel = () => {
    setStopDialogOpen(false);
    setCampaignToStop(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'stopped':
        return 'error';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <PlayArrow />;
      case 'paused':
        return <Pause />;
      case 'stopped':
        return <Stop />;
      case 'completed':
        return <Refresh />;
      default:
        return undefined;
    }
  };

  const getProgressPercentage = (campaign: Campaign) => {
    if (campaign.total_contacts === 0) return 0;
    return Math.round((campaign.completed_contacts / campaign.total_contacts) * 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAccountName = (accountId: number) => {
    const account = accounts.find((acc: any) => acc.id === accountId);
    return account?.name || `Account ${accountId}`;
  };

  const getAgentName = async (agentId: number, accountId: number) => {
    // This would need to be implemented with proper agent lookup
    return `Agent ${agentId}`;
  };

  if (campaignsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (campaignsError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Failed to load campaigns. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      {/* Navigation Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate('/aigent/agents')} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNext fontSize="small" />}>
            <Link color="inherit" onClick={() => navigate('/aigent/agents')} sx={{ cursor: 'pointer' }}>
              Aigent
            </Link>
            <Typography color="text.primary">All Campaigns</Typography>
          </Breadcrumbs>
        </Box>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            All Outbound Calling Campaigns
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage campaigns across all agents and accounts
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Campaigns Table */}
      {campaigns && campaigns.length > 0 ? (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 60 }}>#</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Campaign Name</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Status</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Account</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Agent</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Tag</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Progress</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Interval</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Created</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns && campaigns.length > 0 ? (() => {
                  // Sort campaigns by creation date (newest first for display)
                  const sortedCampaigns = [...campaigns].sort((a, b) => 
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                  );
                  
                  // Create a map of campaign ID to chronological number (oldest = 1, newest = highest)
                  const chronologicalNumbers = [...campaigns]
                    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                    .reduce((acc, campaign, index) => {
                      acc[campaign.id] = index + 1;
                      return acc;
                    }, {} as Record<number, number>);
                  
                  return sortedCampaigns.map((campaign, index) => (
                  <TableRow key={campaign.id} hover>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                        }}
                      >
                        {chronologicalNumbers[campaign.id]}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {campaign.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(campaign.status)}
                        label={campaign.status.toUpperCase()}
                        color={getStatusColor(campaign.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getAccountName(campaign.umbrella_account_id)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        Agent {campaign.agent_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={campaign.tag_name} size="small" color="primary" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 100 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {getProgressPercentage(campaign)}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {campaign.completed_contacts}/{campaign.total_contacts}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={getProgressPercentage(campaign)}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {campaign.interval_minutes}min
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(campaign.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {campaign.status === 'paused' && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PlayArrow />}
                            onClick={() => handleCampaignAction(campaign.id, 'start')}
                            color="success"
                            disabled={loadingCampaignId === campaign.id}
                          >
                            {loadingCampaignId === campaign.id ? 'Starting...' : 'Start'}
                          </Button>
                        )}

                        {campaign.status === 'active' && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Pause />}
                            onClick={() => handleCampaignAction(campaign.id, 'pause')}
                            color="warning"
                          >
                            Pause
                          </Button>
                        )}

                        {campaign.status === 'paused' && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PlayArrow />}
                            onClick={() => handleCampaignAction(campaign.id, 'resume')}
                            color="primary"
                          >
                            Resume
                          </Button>
                        )}

                        {(campaign.status === 'active' || campaign.status === 'paused') && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Stop />}
                            onClick={() => handleStopClick(campaign)}
                            color="error"
                          >
                            Stop
                          </Button>
                        )}

                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleViewDetails(campaign.id)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ));
                })() : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" color="text.secondary" align="center">
              No campaigns found. Create your first campaign to get started.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Stop Campaign Confirmation Dialog */}
      <Dialog
        open={stopDialogOpen}
        onClose={handleStopCancel}
        aria-labelledby="stop-dialog-title"
        aria-describedby="stop-dialog-description"
      >
        <DialogTitle id="stop-dialog-title">
          Stop Campaign
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="stop-dialog-description">
            Are you sure you want to stop the campaign "{campaignToStop?.name}"? 
            This action cannot be undone and will permanently stop the campaign.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStopCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleStopConfirm} color="error" variant="contained">
            Stop Campaign
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AllCampaignsPage;
